import { useEffect, useState } from 'react'
import {
  CTA_CTE,
  ENVIO_A_CARGO_COMPRADOR,
  ENVIO_GRATIS,
  RETIRA_COMPRADOR
} from '../../../constants/mediosEnviosPagos.js'
import { toastErrorLog } from '../../../helpers/toastErrorLog'
import { calcularTotalItemSinIva } from '../../helpers/calcularPrecioTotal'
import * as impuestoService from '../../servicios/impuestoService'

const INSCRIPTO = 1
const EXENTO = 3
const ALICUOTA_PERCEPCION = 3
const ALICUOTA_MAX = 4
const ID_BUENOS_AIRES = 1
export const ID_CABA = 0
const ID_ENTRE_RIOS = 5
const ID_JUJUY = 6
const PADRONES_PROVINCIAS = [ID_CABA, ID_BUENOS_AIRES, ID_ENTRE_RIOS, ID_JUJUY]
export const ALIAS_CABA = [
  'caba',
  'capital federal',
  'ciudad autonoma de buenos aires',
  'ciudad de buenos aires',
  'cap fed'
]

const obtenerIdItem = (item) => item.idPresupuestoItem || item.idPublicacion

const idProvinciaValida = (idProvincia) =>
  idProvincia !== null &&
  idProvincia !== undefined &&
  String(idProvincia).trim() !== '' &&
  !Number.isNaN(Number(idProvincia))

export const usePercepciones = ({
  vendedor,
  empid,
  idMedioEnvio,
  direccionEntrega,
  medioPagoSeleccionado,
  listaEnvioSeleccionada,
  provinciasPorNombre,
  provinciasPorId,
  domiciliosVendedor,
  obtenerPrecioEnvioPorItem,
  todosMediosEnvioSeleccionados,
  normalizarNombre
}) => {
  const [percepciones, setPercepciones] = useState([])
  const [alicuota, setAlicuota] = useState([])
  const [datosFiscalesComprador, setDatosFiscalesComprador] = useState([])
  const [datosFiscalesVendedor, setDatosFiscalesVendedor] = useState([])

  useEffect(() => {
    if (empid) {
      obtenerAlicuotaPercepcion()
    }
  }, [empid])

  const obtenerAlicuotaPercepcion = async () => {
    const { data } = await impuestoService.obtenerAlicuota(empid)
    if (!Array.isArray(data) || data.length === 0) {
      setAlicuota(null)
      return
    }
    const alicuotaData = data.map((d) => {
      const alicuotaNormalizada =
        typeof d.alicuotaPadron === 'string'
          ? parseFloat(d.alicuotaPadron.replace(',', '.'))
          : Number(d.alicuotaPadron)
      return {
        alicuotaPadron: alicuotaNormalizada,
        idProvincia: d.idProvincia,
        nombreProvincia: d.nombreProvincia
      }
    })
    setAlicuota(alicuotaData)
  }

  const calcularPercepciones = (pedidoItems) => {
    const domicilioPrincipalVendedor = domiciliosVendedor.find((d) => d.domicilioPrincipal === true)

    const itemsConPercepcion = pedidoItems.filter(
      (item) => Number(medioPagoSeleccionado[obtenerIdItem(item)]) !== CTA_CTE
    )

    const itemsQueRequierenDomicilio = itemsConPercepcion
      .map((item) => {
        const medio = idMedioEnvio?.[obtenerIdItem(item)]

        if (medio === RETIRA_COMPRADOR && domicilioPrincipalVendedor?.provincia) {
          const idProvinciaVendedor =
            provinciasPorNombre.get(normalizarNombre(domicilioPrincipalVendedor.provincia)) ?? 0
          return {
            ...item,
            esRetiraComprador: true,
            provinciaVendedor: idProvinciaVendedor
          }
        }

        if (medio === ENVIO_GRATIS || medio === ENVIO_A_CARGO_COMPRADOR) {
          return item
        }

        return null
      })
      .filter(Boolean)

    // Provincias en donde se realiza la entrega
    const provinciasEntrega = itemsQueRequierenDomicilio.reduce((acc, item) => {
      const idItem = obtenerIdItem(item)
      const provincia = item.esRetiraComprador
        ? Number(item.provinciaVendedor)
        : direccionEntrega?.[idItem]?.idProvincia === ID_CABA ||
            ALIAS_CABA.includes(
              direccionEntrega?.[idItem]?.localidad?.toLowerCase() ||
                direccionEntrega?.[idItem]?.provincia?.toLowerCase()
            )
          ? ID_CABA
          : Number(direccionEntrega?.[idItem]?.idProvincia)
      if (idProvinciaValida(provincia) && !acc.includes(provincia)) {
        acc.push(provincia)
      }
      return acc
    }, [])

    // Provincias donde el comprador tiene cargados datos fiscales
    const provinciasConDatosComprador = datosFiscalesComprador.reduce((acc, df) => {
      if (Number(df.idCondicionFrenteIIBB) === INSCRIPTO) {
        const provincia = Number(df.idProvincia)
        if (!acc.includes(provincia)) {
          acc.push(provincia)
        }
      }
      return acc
    }, [])

    const provinciasExentasComprador = datosFiscalesComprador.reduce((acc, df) => {
      if (Number(df.idCondicionFrenteIIBB) === EXENTO) {
        const provincia = Number(df.idProvincia)
        if (!acc.includes(provincia)) {
          acc.push(provincia)
        }
      }
      return acc
    }, [])

    //Carga la provincias en donde el vendedor es agente de percepción
    const provinciasVendedorAgente = datosFiscalesVendedor.reduce((acc, df) => {
      if (df.esAgentePercepcion === true) {
        const provincia = Number(df.idProvincia)
        if (!acc.includes(provincia)) {
          acc.push(provincia)
        }
      }
      return acc
    }, [])

    //Provincias donde se debe aplicar percepciones
    const provinciasAplicables = provinciasEntrega.filter((provincia) =>
      provinciasVendedorAgente.includes(Number(provincia))
    )

    // Item con envío a cargo del comprador
    const itemConEnvioComprador = itemsConPercepcion.find((item) => {
      const idItem = obtenerIdItem(item)
      return (
        Number(idMedioEnvio?.[idItem]) === ENVIO_A_CARGO_COMPRADOR &&
        !!listaEnvioSeleccionada?.[idItem]
      )
    })

    const envioNetoUnico = itemConEnvioComprador
      ? obtenerPrecioEnvioPorItem(itemConEnvioComprador)
      : 0

    const percepcionesMap = new Map()

    itemsConPercepcion.forEach((item) => {
      const idItem = obtenerIdItem(item)
      const itemConDomicilio = itemsQueRequierenDomicilio.find((i) => obtenerIdItem(i) === idItem)

      let idProvincia
      if (itemConDomicilio?.esRetiraComprador) {
        idProvincia = Number(itemConDomicilio.provinciaVendedor)
      } else if (
        direccionEntrega?.[idItem]?.idProvincia === ID_CABA ||
        ALIAS_CABA.includes(
          direccionEntrega?.[idItem]?.localidad?.toLowerCase() ||
            direccionEntrega?.[idItem]?.provincia?.toLowerCase()
        )
      ) {
        idProvincia = ID_CABA
      } else {
        idProvincia = Number(direccionEntrega?.[idItem]?.idProvincia)
      }

      if (!provinciasAplicables.includes(idProvincia)) {
        return
      }

      if (provinciasExentasComprador.includes(idProvincia)) {
        return
      }

      const compradorTieneDatosEnProvincia = provinciasConDatosComprador.includes(idProvincia)

      let baseImponibleItem = calcularTotalItemSinIva(item)

      if (itemConEnvioComprador?.idPublicacion === item.idPublicacion && envioNetoUnico > 0) {
        baseImponibleItem += envioNetoUnico
      }

      if (baseImponibleItem <= 0) {
        return
      }

      if (PADRONES_PROVINCIAS.includes(idProvincia)) {
        const alicuotaValida = alicuota?.find(
          (a) =>
            a.idProvincia === idProvincia &&
            a.alicuotaPadron !== null &&
            !isNaN(a.alicuotaPadron) &&
            a.alicuotaPadron > 0
        )
        if (!alicuotaValida) {
          return
        }
      }

      let alicuotaFinal
      if (!compradorTieneDatosEnProvincia) {
        alicuotaFinal = ALICUOTA_MAX
      } else {
        alicuotaFinal = PADRONES_PROVINCIAS.includes(idProvincia)
          ? alicuota?.find((a) => a.idProvincia === idProvincia).alicuotaPadron
          : ALICUOTA_PERCEPCION
      }

      if (percepcionesMap.has(idProvincia)) {
        percepcionesMap.get(idProvincia).baseImponible += baseImponibleItem
      } else {
        percepcionesMap.set(idProvincia, {
          idProvincia,
          nombreProvincia: provinciasPorId.get(idProvincia),
          alicuota: alicuotaFinal,
          baseImponible: baseImponibleItem
        })
      }
    })

    return Array.from(percepcionesMap.values())
      .filter((p) => p.alicuota > 0 && p.baseImponible > 0)
      .map((p) => ({
        ...p,
        monto: (p.baseImponible * p.alicuota) / 100
      }))
  }

  useEffect(() => {
    if (!vendedor) return

    if (!todosMediosEnvioSeleccionados) {
      setPercepciones([])
      return
    }

    setPercepciones(calcularPercepciones(vendedor.pedidoItems))
  }, [
    todosMediosEnvioSeleccionados,
    idMedioEnvio,
    direccionEntrega,
    listaEnvioSeleccionada,
    datosFiscalesComprador,
    datosFiscalesVendedor,
    vendedor?.pedidoItems
  ])

  /**
   * Persiste las percepciones calculadas para un pedido ya creado.
   * @param {{idPedido: number}} pedidoCreado - Pedido recién creado.
   * @param {Array<{idProvincia: number, alicuota: number, monto: number, baseImponible: number}>} percepcionesDelPedido
   */
  const guardarPercepciones = async (pedidoCreado, percepcionesDelPedido) => {
    try {
      for (const p of percepcionesDelPedido) {
        await impuestoService.insertar({
          idPedido: pedidoCreado.idPedido,
          idProvincia: p.idProvincia,
          tipoImpuesto: 'P',
          alicuota: p.alicuota,
          monto: p.monto,
          baseImponible: p.baseImponible
        })
      }
    } catch (error) {
      const mensaje = `Ocurrió un error al guardar las percepciones.`
      toastErrorLog(error, mensaje, 'impuesto')
    }
  }

  return {
    percepciones,
    datosFiscalesComprador,
    datosFiscalesVendedor,
    calcularPercepciones,
    guardarPercepciones,
    setDatosFiscalesComprador,
    setDatosFiscalesVendedor
  }
}
